import { Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { EmptyState, Skeleton } from '@hrms/ui';
import { RequireAuth } from './session/RequireAuth.js';
import { RequirePermission } from './session/RequirePermission.js';
import { PageErrorBoundary } from './components/PageErrorBoundary.js';
import { isPlatformHost } from './lib/platformHost.js';
import { useClarity } from './lib/clarity.js';
import { useClarityTags } from './session/useClarityTags.js';
// The first load: the frame, Home, and the pages a sign-in or a company switch passes through.
// Every other page is lazy (routes/pages.ts) — see that file's header for why.
import { AppShell } from './routes/AppShell.js';
import { Home } from './routes/Home.js';
import { Login } from './routes/Login.js';
// The phone app's first screen on a fresh install, so it loads with the first bundle like Login.
import { IntroPage } from './features/intro/IntroPage.js';
import { SessionHandoff } from './routes/SessionHandoff.js';
// Zoho Meeting's OAuth callback lands on the central app. origin and bounces to the company's own
// subdomain before any session exists — small, so it is not lazy (see the public route below).
import { ZohoCallbackGate } from './features/settings/ZohoCallbackGate.js';
import { useTravelModuleStatus } from './features/travel/common/useTravelModuleStatus.js';
import {
  PlatformPortalApp,
  Signup,
  PrivacyPage,
  TermsPage,
  VerifyEmail,
  ForgotPassword,
  ForgotPasswordByPhone,
  ResetPassword,
  SsoStart,
  SsoCallback,
  CareersLayout,
  CareersListPage,
  PostingDetailPage,
  AgencyPortalPage,
  ApplicationStatusPage,
  OfferResponsePage,
  SlotOfferPage,
  AssessmentPage,
  VerifyCertificatePage,
  PublicLiveJoinPage,
  SignedInLiveJoinPage,
  AdminArea,
  WelcomeSetupPage,
  SetupPage,
  ZohoOAuthCallbackPage,
  PerformanceTabs,
  PerformanceDashboard,
  GoalsOverview,
  GoalCreate,
  GoalDetail,
  TeamGoals,
  FeedbackHome,
  OneOnOneDetail,
  ReviewList,
  ReviewDetail,
  MeritCyclePicker,
  MeritWorksheet,
  MySkills,
  PerformanceInsights,
  ImprovementPlanList,
  ImprovementPlanCreate,
  ImprovementPlanDetail,
  Leave,
  LeaveRequestDetail,
  TeamCalendar,
  Holidays,
  Policies,
  Approvals,
  InboxPage,
  AttendancePage,
  TimesheetPage,
  ProjectsPage,
  Claims,
  LoansAdvancesPage,
  MyPayslipPage,
  MyDetailsPage,
  MyOnboardingPage,
  MyExitPage,
  MyExitInterviewPage,
  MyHandoverPage,
  TeamExitsPage,
  TeamExitDetailPage,
  MyExitTasksPage,
  MyExitClearancesPage,
  InternalJobsPage,
  MyReferralsPage,
  MyInterviewsPage,
  MyInterviewPage,
  MyLearningPage,
  LearningCourseRoute,
  CourseLinkPage,
  CertificatesPage,
  LegacyTrainingRedirect,
  ChatPage,
  DirectoryPage,
  OrgChartPage,
  MyDocumentsPage,
  MyPoliciesPage,
  MyPolicyPage,
  MyAssets,
  AssetDashboard,
  AssetInventory,
  AssetDetail,
  RequestsAllocation,
  RequestApprovalDetail,
  AssetOperations,
  MaintenanceIssues,
  MaintenanceDetail,
  AdministrationShell,
  AssetConfigurationPage,
  VendorsPage,
  AssetBatchesPage,
  AssetHistoryAuditReports,
  RecognitionPage,
  CertificatePage,
  SocialWallPage,
  MySurveysPage,
  TakeSurveyPage,
  AnnouncementFeedPage,
  AnnouncementPage,
  CelebrationsPage,
  MyHelpdeskPage,
  MyTicketPage,
  TicketLinkRedirect,
  TravelDeskLayout,
  TravelTripLinkRedirect,
  MyTripsPage,
  TripDetailPage,
  ManagerTravelApprovalsPage,
  NewTripPage,
  TravellerProfilePage,
  TravelReportsPage,
  WhoIsTravellingPage,
  DeskTeamsPage,
  DeskSlaDashboardPage,
  DeskQueuePage,
  DeskBookingPanel,
  DeskTripPage,
  SharedBookingView,
  DeskHistoryPage,
  DeskCreditsPage,
  DeskVendorsPage,
  SubmitTripExpensesPage,
  SettlementReviewPage,
  SettlementDetailPage,
  OutstandingAdvancesPage,
  AdvanceRecoveryPage,
  WriteOffsPage,
} from './routes/pages.js';

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Skeleton className="h-64" />}>{children}</Suspense>;
}

/**
 * Advance notifications (`expense.advance.overdue`, `.recovery_scheduled`, `.recovery_reversed`,
 * `.write_off_decided`) were catalogued as `/claims/advances/:advanceId`, which no screen owns.
 * With Travel on, the advance's recovery page (ledger, payroll schedule, write-off) answers it;
 * otherwise the Claims screen's Advances tab opens the advance, as the other advance links do.
 */
function AdvanceLinkRedirect() {
  const { advanceId = '' } = useParams<{ advanceId: string }>();
  const travel = useTravelModuleStatus();
  if (travel.isLoading) return <Skeleton className="h-64" />;
  const id = encodeURIComponent(advanceId);
  return (
    <Navigate
      to={
        travel.enabled ? `/travel/money/advances/${id}` : `/claims?tab=advances&openAdvance=${id}`
      }
      replace
    />
  );
}

export function App() {
  // Session recording for the whole app; a no-op on the phone app and the management host.
  useClarity();
  useClarityTags();

  // The management hostname serves the portal and nothing else — see lib/platformHost.ts.
  if (isPlatformHost()) {
    return (
      <PageErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <PlatformPortalApp />
        </Suspense>
      </PageErrorBoundary>
    );
  }

  // The outer boundaries catch the public pages (sign-up, careers, legal…). Pages inside the app
  // frame are caught by AppShell's own boundaries first, so the sidebar and top bar stay on screen.
  // NOT keyed by the path, unlike AppShell's: a key here would remount the whole app — frame,
  // socket and all — on every navigation. Its "Reload" button is the way out.
  return (
    <PageErrorBoundary>
      <Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </Suspense>
    </PageErrorBoundary>
  );
}

function RouteFallback() {
  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <Skeleton className="h-64" />
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* `/signup/*`: the four sign-up screens (number, code, team, company) are one mounted page. */}
      <Route path="/signup/*" element={<Signup />} />
      {/* Phone app only; IntroPage sends anyone not eligible (introEligible) on to /signup. */}
      <Route path="/intro" element={<IntroPage />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/forgot-password/phone" element={<ForgotPasswordByPhone />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/session-handoff" element={<SessionHandoff />} />
      <Route path="/auth/:provider/start" element={<SsoStart />} />
      <Route path="/auth/:provider/callback" element={<SsoCallback />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Zoho Meeting's OAuth redirect target (ZOHO_REDIRECT_URI). Zoho redirects to ONE registered
          URL on the central app.<APP_DOMAIN> origin, which has no session, so the path is public:
          ZohoCallbackGate bounces to the company subdomain named in `state` first, and only then
          does the usual guarded flow (sign-in, integration:read) run. The /admin twin is in
          AdminArea. */}
      <Route path="/settings/integrations/zoho/callback" element={<ZohoCallbackGate />}>
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            <Route
              index
              element={
                <RequirePermission permission="integration:read">
                  <Suspense fallback={<Skeleton className="h-64" />}>
                    <ZohoOAuthCallbackPage />
                  </Suspense>
                </RequirePermission>
              }
            />
          </Route>
        </Route>
      </Route>

      {/* The public career site and offer-acceptance flow — no session, same trust tier as the
          auth routes above. `/careers/offer/:offerToken` is declared before the `:orgSlug`
          catch-alls; React Router ranks a static "offer" segment over a dynamic one at the same
          depth regardless of declaration order, but the explicit ordering keeps the intent obvious
          on read. */}
      <Route element={<CareersLayout />}>
        <Route path="/careers/offer/:offerToken" element={<OfferResponsePage />} />
        <Route path="/careers/slot-offer/:token" element={<SlotOfferPage />} />
        <Route path="/careers/assessment/:token" element={<AssessmentPage />} />
        <Route path="/careers/:orgSlug" element={<CareersListPage />} />
        <Route path="/careers/:orgSlug/status" element={<ApplicationStatusPage />} />
        <Route path="/careers/:orgSlug/agency/:agencyToken" element={<AgencyPortalPage />} />
        <Route path="/careers/:orgSlug/:postingSlug" element={<PostingDetailPage />} />
      </Route>

      {/* Public certificate verification — no session, no app shell. Reached only via a
          certificate's printed/shared QR/link, never from the sidebar. */}
      <Route
        path="/verify/:code"
        element={
          <Suspense fallback={<Skeleton className="h-64" />}>
            <VerifyCertificatePage />
          </Suspense>
        }
      />
      {/* The certificate PDF's QR code and "Copy verify link" both emit this longer form. */}
      <Route
        path="/certificates/verify/:code"
        element={
          <Suspense fallback={<Skeleton className="h-64" />}>
            <VerifyCertificatePage />
          </Suspense>
        }
      />

      {/* A live training session's personal join link (sent in each invite). Public like
          /verify/:code: the token alone identifies the invitee; no session, no app shell. */}
      <Route
        path="/j/:token"
        element={
          <Suspense fallback={<Skeleton className="h-64" />}>
            <PublicLiveJoinPage />
          </Suspense>
        }
      />

      <Route element={<RequireAuth />}>
        {/* The shareable live-session link ("Copy link" / WhatsApp). Signed-in, so RequireAuth
            sends a signed-out visitor to /login and back here; outside the shell because it only
            records the click and hands over to the meeting. */}
        <Route
          path="/training/live/:sessionId/join"
          element={
            <Suspense fallback={<Skeleton className="h-64" />}>
              <SignedInLiveJoinPage />
            </Suspense>
          }
        />
        {/* First-run welcome setup: full screen, outside the app shell (FR-4). */}
        <Route
          path="/welcome"
          element={
            <Suspense fallback={<Skeleton className="h-64" />}>
              <WelcomeSetupPage />
            </Suspense>
          }
        />
        <Route element={<AppShell />}>
          <Route path="/" element={<Home />} />
          <Route
            path="/setup"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <SetupPage />
              </Suspense>
            }
          />
          <Route
            path="/chat"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <ChatPage />
              </Suspense>
            }
          />
          <Route
            path="/chat/:conversationId"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <ChatPage />
              </Suspense>
            }
          />

          {/* A3 (performance-management-atomic-canyon plan, Phase A3): only the 5 top-level
              Performance tabs nest under the in-page tab strip. Every drill-in route (goal
              detail, review detail, 1:1 detail, improvement plans) stays a flat sibling below,
              exactly where it was before — a full-page detail view shouldn't render under a tab
              strip with nothing meaningfully active. */}
          <Route element={<PerformanceTabs />}>
            <Route path="/performance" element={<PerformanceDashboard />} />
            <Route path="/performance/goals" element={<GoalsOverview />} />
            <Route path="/performance/feedback" element={<FeedbackHome />} />
            <Route path="/performance/reviews" element={<ReviewList />} />
            <Route
              path="/performance/insights"
              element={<PerformanceInsights defaultView="employee" />}
            />
          </Route>

          <Route path="/performance/goals/new" element={<GoalCreate />} />
          <Route path="/performance/goals/team" element={<TeamGoals />} />
          <Route path="/performance/goals/:goalId" element={<GoalDetail />} />
          <Route path="/performance/feedback/one-on-ones/:meetingId" element={<OneOnOneDetail />} />
          {/* PRF-9/PRF-29: the manager's own merit worksheet - self-service, distinct from the HR
              "Merit" tab on the admin roster (/admin/performance/review-cycles/:cycleId/roster
              ?tab=merit). A static segment ("merit"), so it wins over the dynamic
              /performance/reviews/:reviewId route below regardless of declaration order (React
              Router's ranking). The bare /merit path is the menu's cycle picker. */}
          <Route path="/performance/reviews/merit" element={<MeritCyclePicker />} />
          <Route path="/performance/reviews/merit/:cycleId" element={<MeritWorksheet />} />
          {/* PRF-16: "My skills" — a static segment, wins over :reviewId below regardless of
              declaration order (React Router's ranking, same as merit/:cycleId above). */}
          <Route path="/performance/reviews/skills" element={<MySkills />} />
          <Route path="/performance/reviews/:reviewId" element={<ReviewDetail />} />
          {/* PRF-3: the dashboard's pending-actions feed links peer-feedback items straight to this
              URL (see getPendingActions' `deepLink` in apps/api/src/routes/performance-dashboard.ts),
              which 404'd — there was no matching route. `ReviewDetail` already renders its
              `PeerFeedbackPanel` inline, so this reuses it as-is rather than 404ing; it doesn't yet
              scroll to or highlight that section specifically (no anchor/id exists on the panel
              today) — landing on the plain review detail page is the deliberate first step, per the
              gap-remediation plan, until that anchor is built. */}
          <Route path="/performance/reviews/:reviewId/peer-feedback" element={<ReviewDetail />} />
          <Route path="/performance/improvement-plans" element={<ImprovementPlanList />} />
          <Route path="/performance/improvement-plans/new" element={<ImprovementPlanCreate />} />
          <Route
            path="/performance/improvement-plans/:planId"
            element={<ImprovementPlanDetail />}
          />
          <Route path="/claims" element={<Claims />} />
          <Route path="/claims/advances/:advanceId" element={<AdvanceLinkRedirect />} />

          {/* Travel Desk (ADR 0030). `/travel/approvals`, `/travel/new`, `/travel/profile`,
              `/travel/desk` and `/travel/who-is-travelling` are static segments, which React Router
              ranks above `/travel/:travelRequestId` whatever the declaration order. My trips, the
              trip page and approvals are open to anyone signed in: the API shows a person only the
              trips they may see, and an assigned approver may hold no travel permission at all. */}
          <Route
            path="/travel"
            element={
              <LazyPage>
                <MyTripsPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/new"
            element={
              <RequirePermission permission="travel:request">
                <LazyPage>
                  <NewTripPage />
                </LazyPage>
              </RequirePermission>
            }
          />
          <Route
            path="/travel/profile"
            element={
              <RequirePermission permission="travel:request">
                <LazyPage>
                  <TravellerProfilePage />
                </LazyPage>
              </RequirePermission>
            }
          />
          <Route
            path="/travel/approvals"
            element={
              <LazyPage>
                <ManagerTravelApprovalsPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/reports"
            element={
              <RequirePermission permission="travel:read">
                <LazyPage>
                  <TravelReportsPage />
                </LazyPage>
              </RequirePermission>
            }
          />
          <Route
            path="/travel/who-is-travelling"
            element={
              <RequirePermission permission="travel:read">
                <LazyPage>
                  <WhoIsTravellingPage />
                </LazyPage>
              </RequirePermission>
            }
          />
          {/* The desk: queue, booking panel, trip view, group bookings, history, teams and SLA.
              Work queues and tables, so they render at the admin area's compact density. */}
          <Route element={<TravelDeskLayout />}>
            <Route
              path="/travel/desk"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskQueuePage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/history"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskHistoryPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/credits"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskCreditsPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/vendors"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskVendorsPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/teams"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskTeamsPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/sla"
              element={
                <RequirePermission permission="travel.desk:manage">
                  <LazyPage>
                    <DeskSlaDashboardPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/bookings/:bookingId"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskBookingPanel />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/trips/:tripId"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <DeskTripPage />
                  </LazyPage>
                </RequirePermission>
              }
            />
            <Route
              path="/travel/desk/shared/:sharedBookingId"
              element={
                <RequirePermission permission="travel.desk:read">
                  <LazyPage>
                    <SharedBookingView />
                  </LazyPage>
                </RequirePermission>
              }
            />
          </Route>
          {/* A trip's money (Phase 4, TD-4.10). `/travel/money/...` is a static segment, so it ranks
              above `/travel/:travelRequestId/...`. Settlement rounds, an advance's recovery page and
              write-offs are open to anyone signed in: the API shows the traveller, the assigned
              approver and finance only what they may see (404 otherwise), and an approver may hold
              no travel permission at all. Every page wraps itself in `TravelModuleGate`. */}
          <Route
            path="/travel/money"
            element={<Navigate to="/travel/money/settlements" replace />}
          />
          <Route
            path="/travel/money/settlements"
            element={
              <LazyPage>
                <SettlementReviewPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/money/settlements/:settlementId"
            element={
              <LazyPage>
                <SettlementDetailPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/money/advances"
            element={
              <RequirePermission permission="expense.advance:manage">
                <LazyPage>
                  <OutstandingAdvancesPage />
                </LazyPage>
              </RequirePermission>
            }
          />
          <Route
            path="/travel/money/advances/:advanceId"
            element={
              <LazyPage>
                <AdvanceRecoveryPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/money/write-offs"
            element={
              <LazyPage>
                <WriteOffsPage />
              </LazyPage>
            }
          />
          <Route
            path="/travel/:travelRequestId/expenses"
            element={
              <RequirePermission permission="travel:request">
                <LazyPage>
                  <SubmitTripExpensesPage />
                </LazyPage>
              </RequirePermission>
            }
          />
          {/* Notification links were catalogued as /travel/trips/:id; send them to the trip's page. */}
          <Route path="/travel/trips/:travelRequestId" element={<TravelTripLinkRedirect />} />
          <Route
            path="/travel/:travelRequestId"
            element={
              <LazyPage>
                <TripDetailPage />
              </LazyPage>
            }
          />
          <Route
            path="/loans"
            element={
              <RequirePermission permission="payroll.loan:request">
                <LoansAdvancesPage />
              </RequirePermission>
            }
          />
          <Route path="/my-details" element={<MyDetailsPage />} />
          {/* Self-service payroll. Eager, not in the admin chunk: this is the employee path.
              Any-of, because the two panels are gated separately by the API and holding either
              one is reason enough to see the page. */}
          <Route
            path="/payslip"
            element={
              <RequirePermission permission={['payslip.own:read', 'payroll.payment.own:read']}>
                <MyPayslipPage />
              </RequirePermission>
            }
          />
          <Route path="/leave" element={<Leave />} />
          <Route path="/leave/calendar" element={<TeamCalendar />} />
          <Route path="/leave/holidays" element={<Holidays />} />
          <Route path="/leave/policies" element={<Policies />} />
          <Route path="/leave/requests/:leaveRequestId" element={<LeaveRequestDetail />} />
          <Route
            path="/engage/surveys"
            element={
              <RequirePermission permission="survey:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MySurveysPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/surveys/:surveyId"
            element={
              <RequirePermission permission="survey:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <TakeSurveyPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/organisation/policies"
            element={
              <RequirePermission permission="policy:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MyPoliciesPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/organisation/policies/:policyId"
            element={
              <RequirePermission permission="policy:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MyPolicyPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/recognition"
            element={
              <RequirePermission permission="recognition:give">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <RecognitionPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/recognition/certificates/:recognitionId"
            element={
              <RequirePermission permission="recognition:give">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <CertificatePage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/social"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <SocialWallPage />
              </Suspense>
            }
          />
          <Route
            path="/engage/announcements"
            element={
              <RequirePermission permission="announcement:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AnnouncementFeedPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/celebrations"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CelebrationsPage />
              </Suspense>
            }
          />
          <Route
            path="/helpdesk"
            element={
              <RequirePermission permission="ticket:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MyHelpdeskPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/engage/announcements/:announcementId"
            element={
              <RequirePermission permission="announcement:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AnnouncementPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/helpdesk/:ticketId"
            element={
              <RequirePermission permission="ticket:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MyTicketPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/helpdesk/tickets/:ticketId"
            element={
              <RequirePermission permission="ticket:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <TicketLinkRedirect />
                </Suspense>
              </RequirePermission>
            }
          />
          {/* The unified inbox: every approval waiting on the caller plus their notifications.
              Signed-in only, like `GET /inbox` — an assigned approver may hold no approving role. */}
          <Route path="/inbox" element={<InboxPage />} />
          {/* The old leave-only approvals URL — bookmarks, the `leave.*` notification hrefs and
              the sidebar entry all point here — lands on the inbox filtered to leave. The full
              leave queue (decided history, batch approve, balances) moved to /leave/approvals,
              which is where a leave item's "Open" link goes. */}
          <Route path="/approvals" element={<Navigate to="/inbox?module=leave" replace />} />
          <Route
            path="/leave/approvals"
            element={
              <RequirePermission permission="leave:approve">
                <Approvals />
              </RequirePermission>
            }
          />
          {/* Interviewers' own view — self-scoped on the server, so no permission wrapper: anyone
              on an interview panel lands here from their notification (recruitment bug 11). */}
          {/* The bell's links for requisition and offer approvals use the shared catalogue's paths (which
              the phone app also reads); the web screens are the admin lists. A stored notification
              still carries the old path, so it is redirected here rather than rewritten. */}
          <Route
            path="/recruitment/requisitions/:id"
            element={<Navigate to="/admin/recruitment/requisitions" replace />}
          />
          <Route
            path="/recruitment/offers/:id"
            element={<Navigate to="/admin/recruitment/offers" replace />}
          />
          <Route path="/my-interviews" element={<MyInterviewsPage />} />
          <Route path="/jobs" element={<InternalJobsPage />} />
          <Route path="/my-interviews/:interviewId" element={<MyInterviewPage />} />
          <Route
            path="/my-referrals"
            element={
              <RequirePermission permission="referral:read">
                <MyReferralsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/organisation/directory"
            element={
              <RequirePermission permission="employee.directory:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <DirectoryPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/organisation/org-chart"
            element={
              <RequirePermission permission="org_chart:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <OrgChartPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/organisation/documents"
            element={
              <RequirePermission permission="employee.document:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MyDocumentsPage />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/my-onboarding"
            element={
              <RequirePermission permission="onboarding:read">
                <MyOnboardingPage />
              </RequirePermission>
            }
          />
          {/* The onboarding work queue is routed inside AdminArea (/admin/onboarding/queue). The old
              dashboard and per-person process pages are unrouted (consolidated into
              OnboardingProbationPage at /admin/onboarding). Employees answer their Day 7/30/60/90
              pulse check-ins on /my-onboarding above. */}
          {/* Offboarding — employee's own exit, routeMeta(null) (self), same as /leave/-claims
              above: no RequirePermission wrapper, everyone signed in may reach their own exit. */}
          <Route path="/exit" element={<MyExitPage />} />
          <Route path="/exit/interview" element={<MyExitInterviewPage />} />
          <Route path="/exit/handover" element={<MyHandoverPage />} />
          <Route
            path="/team/exits"
            element={
              <RequirePermission permission="offboarding.team:read">
                <TeamExitsPage />
              </RequirePermission>
            }
          />
          {/* Deliberately NOT RequirePermission-wrapped — listMyTasks is routeMeta(null) (assignee
              = self), and offboarding's task-assignment notifications (task_assigned/due/overdue)
              link here for ANY assignee role (IT, finance, HR), not just managers. Gating this
              route on offboarding.team:read would 403 exactly the people those notifications are
              sent to. The sidebar entry still carries that permission, as a manager-facing nav
              courtesy — see nav.ts. */}
          <Route path="/team/exits/tasks" element={<MyExitTasksPage />} />
          {/* Unwrapped for the same reason as /team/exits/tasks: listMyClearances is signed-in only and
              checks the assignment itself, and a clearance owner (manager, IT, finance, a named
              person) lands here from the fnf_settlement.clearance_assigned notification. */}
          <Route path="/team/exits/clearances" element={<MyExitClearancesPage />} />
          <Route
            path="/team/exits/:processId"
            element={
              <RequirePermission permission="offboarding.team:read">
                <TeamExitDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/asset-management/dashboard"
            element={
              <RequirePermission permission="asset:analytics:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AssetDashboard />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/asset-management/my-assets"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <MyAssets />
              </Suspense>
            }
          />
          <Route
            path="/asset-management/assets"
            element={
              <RequirePermission permission="asset:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AssetInventory />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/asset-management/assets/:assetId"
            element={
              <RequirePermission permission="asset:read">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AssetDetail />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/asset-management/requests"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <RequestsAllocation />
              </Suspense>
            }
          />
          <Route
            path="/asset-management/requests/:requestId"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <RequestApprovalDetail />
              </Suspense>
            }
          />
          <Route
            path="/asset-management/operations"
            element={
              <RequirePermission permission="asset:assign">
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <AssetOperations />
                </Suspense>
              </RequirePermission>
            }
          />
          {/* Administration — Configuration + Vendors, the admin screens over the asset register
           * above. Own layout route (AdministrationShell renders the pill-tab header + Outlet),
           * matching AdminArea.tsx's AdminLayout pattern one level down instead of at the top. */}
          <Route
            path="/asset-management/administration"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <AdministrationShell />
              </Suspense>
            }
          >
            <Route
              index
              element={
                <RequirePermission permission="asset.category:read">
                  <AssetConfigurationPage />
                </RequirePermission>
              }
            />
            <Route
              path="configuration"
              element={
                <RequirePermission permission="asset.category:read">
                  <AssetConfigurationPage />
                </RequirePermission>
              }
            />
            <Route
              path="vendors"
              element={
                <RequirePermission permission="asset.vendor:read">
                  <VendorsPage />
                </RequirePermission>
              }
            />
            <Route
              path="batches"
              element={
                <RequirePermission permission="asset.batch:read">
                  <AssetBatchesPage />
                </RequirePermission>
              }
            />
            <Route
              path="history"
              element={
                <RequirePermission permission={['asset:read', 'asset:audit:read']}>
                  <AssetHistoryAuditReports />
                </RequirePermission>
              }
            />
            <Route
              path="insights"
              element={
                <RequirePermission permission={['asset:read', 'asset:audit:read']}>
                  <AssetHistoryAuditReports />
                </RequirePermission>
              }
            />
          </Route>
          <Route
            path="/asset-management/maintenance"
            element={
              <RequirePermission
                permission={['asset:maintenance:read', 'asset:maintenance:manage']}
              >
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MaintenanceIssues />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/asset-management/maintenance/:issueId"
            element={
              <RequirePermission
                permission={['asset:maintenance:read', 'asset:maintenance:manage']}
              >
                <Suspense fallback={<Skeleton className="h-64" />}>
                  <MaintenanceDetail />
                </Suspense>
              </RequirePermission>
            }
          />
          <Route
            path="/attendance/*"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <AttendancePage />
              </Suspense>
            }
          />
          <Route
            path="/learning"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <MyLearningPage />
              </Suspense>
            }
          />
          <Route
            path="/learning/certificates"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CertificatesPage />
              </Suspense>
            }
          />
          {/* The shareable course link. Inside RequireAuth, so a signed-out visitor goes to /login
              and comes back here; it only ever opens the caller's own assignment of the course. */}
          <Route
            path="/learning/courses/:courseId"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CourseLinkPage />
              </Suspense>
            }
          />
          {/* The short course link "Copy link" and WhatsApp share: /learning/c/<code>/<name>. */}
          <Route
            path="/learning/c/:code"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CourseLinkPage />
              </Suspense>
            }
          />
          <Route
            path="/learning/c/:code/:slug"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CourseLinkPage />
              </Suspense>
            }
          />
          {/* The longer id form, with the course's readable name after the id — the name is for
              people reading it and is ignored. Kept for links shared before short codes. */}
          <Route
            path="/learning/courses/:courseId/:slug"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <CourseLinkPage />
              </Suspense>
            }
          />
          <Route
            path="/learning/:course"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <LearningCourseRoute />
              </Suspense>
            }
          />
          {/* Old learner links (emails, notifications, bookmarks, shares): /training, /training/:ref,
              /training/courses/:id, /training/certificates → their /learning pages. The live-session
              join link (/training/live/:sessionId/join, above, outside the shell) is more specific
              and still wins. */}
          <Route path="/training/*" element={<LegacyTrainingRedirect />} />
          <Route
            path="/projects/*"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <ProjectsPage />
              </Suspense>
            }
          />
          <Route
            path="/timesheet/*"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <TimesheetPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<Skeleton className="h-64" />}>
                <AdminArea />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <EmptyState
      title="Not built yet"
      description="This section is on the roadmap — the sidebar links ahead of the screens on purpose, so the shape of the product is visible before every screen exists."
    />
  );
}
